import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfile } from '@/hooks/useUserQueries';
import { supabase } from '@/integrations/supabase/client';

/**
 * Auto-opens the finale experience when a program has completed and its
 * summary snapshot has been frozen (profiles.pending_finale_program_id).
 *
 * Rules:
 *  - only fires on the HOME route ('/') so we never yank a user out of a task
 *  - never fires twice for the same program id in a session (handled set)
 *  - skips navigation if the program_summaries row is missing
 */
export function useFinaleGate() {
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const handled = useRef<Set<string>>(new Set());

  const pendingId = (profile as { pending_finale_program_id?: string | null } | null | undefined)
    ?.pending_finale_program_id;

  useEffect(() => {
    if (!pendingId) return;
    if (handled.current.has(pendingId)) return;
    if (location.pathname !== '/') return;

    handled.current.add(pendingId);
    let cancelled = false;

    (async () => {
      const { data } = await supabase
        .from('program_summaries')
        .select('id')
        .eq('program_id', pendingId)
        .maybeSingle();
      if (cancelled || !data) return;
      navigate(`/finale/${pendingId}`);
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingId, location.pathname, navigate]);
}
